import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Globe } from "lucide-react";
import Modal, { Field, Input, Select, GoldButton, PageHeader, EmptyState } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

/**
 * Firm-level Court VC Links. Firms see the platform defaults (read-only)
 * AND can add their own URLs that override the global match.
 */
export default function CourtVcLinksPage() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ court: "", category: "", bench: "", hearing_type: "", url: "", active: true });
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [opts, setOpts] = useState({ courts: [], categories: [], benches: [], hearings: [] });

    const load = () => api.get("/court-vc-links").then(r => setItems(r.data)).catch(e => toast.error(apiError(e)));
    const tableCtrl = useTableControls(items, {
        accessors: { scope: (it) => it.scope === "global" ? "Global" : "Firm" },
    });
    useEffect(() => {
        load();
        Promise.all([
            api.get("/config/courts").catch(() => ({ data: [] })),
            api.get("/config/case_categories").catch(() => ({ data: [] })),
            api.get("/config/benches").catch(() => ({ data: [] })),
            api.get("/config/hearing_types").catch(() => ({ data: [] })),
        ]).then(([co, ca, be, he]) => setOpts({
            courts: (co.data || []).map(x => ({ value: x.name, label: x.name })),
            categories: (ca.data || []).map(x => ({ value: x.name, label: x.name })),
            benches: (be.data || []).map(x => ({ value: x.name, label: x.name })),
            hearings: (he.data || []).map(x => ({ value: x.name, label: x.name })),
        }));
    }, []);

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            if (editId) {
                if (!form.reason || form.reason.trim().length < 5) {
                    toast.error("Reason for change (min 5 chars) is mandatory"); setSaving(false); return;
                }
                const { reason, ...patch } = form;
                await api.patch(`/court-vc-links/${editId}`, patch, { headers: { "X-Change-Reason": reason } });
                toast.success("VC link updated");
            } else {
                const { reason, ...rest } = form;
                await api.post("/court-vc-links", rest);
                toast.success("Firm VC link added");
            }
            setOpen(false); setEditId(null);
            setForm({ court: "", category: "", bench: "", hearing_type: "", url: "", active: true, reason: "" });
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const openEdit = (it) => {
        setEditId(it.id);
        setForm({
            court: it.court || "",
            category: it.category || "",
            bench: it.bench || "",
            hearing_type: it.hearing_type || "",
            url: it.url || "",
            active: it.active !== false,
            reason: "",
        });
        setOpen(true);
    };

    const openAdd = () => {
        setEditId(null);
        setForm({ court: "", category: "", bench: "", hearing_type: "", url: "", active: true, reason: "" });
        setOpen(true);
    };

    const remove = async (id) => {
        const reason = window.prompt("Reason for deleting this VC link (min 5 chars):");
        if (!reason || reason.trim().length < 5) {
            if (reason !== null) toast.error("Reason is mandatory");
            return;
        }
        try {
            await api.delete(`/court-vc-links/${id}`, { headers: { "X-Change-Reason": reason.trim() } });
            toast.success("Removed");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader
                title="Court VC links"
                subtitle="URLs your firm uses for video-conference hearings. Platform defaults flow down from Super Admin and are marked Global — your firm's entries win when both match."
                action={<GoldButton onClick={openAdd} data-testid="firm-vc-add-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>Add firm link</GoldButton>}
            />
            {items.length === 0 ? (
                <EmptyState title="No VC links yet" subtitle="Add the first one or ask Super Admin to seed defaults."/>
            ) : (
                <>
                <FilterChip ctrl={tableCtrl} className="mb-3"/>
                <div className="bg-muted border border-border">
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-border">
                            <SortableTh field="scope" ctrl={tableCtrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Scope</SortableTh>
                            <SortableTh field="court" ctrl={tableCtrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Court</SortableTh>
                            <SortableTh field="category" ctrl={tableCtrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Category</SortableTh>
                            <SortableTh field="bench" ctrl={tableCtrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Bench</SortableTh>
                            <SortableTh field="hearing_type" ctrl={tableCtrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Hearing type</SortableTh>
                            <SortableTh field="url" ctrl={tableCtrl} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">URL</SortableTh>
                            <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground"></th>
                        </tr></thead>
                        <tbody>
                            {tableCtrl.rows.map(it => {
                                const isGlobal = it.scope === "global";
                                return (
                                    <tr key={it.id} className="border-b border-background" data-testid={`firm-vc-row-${it.id}`}>
                                        <td className="px-4 py-3">
                                            {isGlobal ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border border-border px-2 py-0.5">
                                                    <Globe className="w-3 h-3" strokeWidth={1.5}/> Global
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-primary border border-primary/40 px-2 py-0.5">
                                                    Firm
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">{it.court}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{it.category || "—"}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{it.bench || "—"}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{it.hearing_type || "—"}</td>
                                        <td className="px-4 py-3"><a href={it.url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate inline-block max-w-[260px] font-mono text-xs">{it.url}</a></td>
                                        <td className="px-4 py-3 text-right">
                                            {!isGlobal && (
                                                <div className="inline-flex items-center gap-3">
                                                    <button onClick={() => openEdit(it)} data-testid={`firm-vc-edit-${it.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" strokeWidth={1.5}/></button>
                                                    <button onClick={() => remove(it.id)} data-testid={`firm-vc-delete-${it.id}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? "Edit firm VC link" : "Add firm VC link"} size="lg" testId="firm-vc-modal">
                <form onSubmit={submit} className="space-y-4">
                    <Field label="URL"><Input required type="url" value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://vc.example.gov.in/..." data-testid="firm-vc-url-input"/></Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Court">
                            <Select value={form.court} onChange={e => setForm({...form, court: e.target.value})}
                                options={[{ value: "", label: "— select court —" }, ...opts.courts]} data-testid="firm-vc-court-select"/>
                        </Field>
                        <Field label="Category" hint="Leave blank to match any category.">
                            <Select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                                options={[{ value: "", label: "— any —" }, ...opts.categories]} data-testid="firm-vc-category-select"/>
                        </Field>
                        <Field label="Bench" hint="Leave blank to match any bench.">
                            <Select value={form.bench} onChange={e => setForm({...form, bench: e.target.value})}
                                options={[{ value: "", label: "— any —" }, ...opts.benches]} data-testid="firm-vc-bench-select"/>
                        </Field>
                        <Field label="Hearing type" hint="Leave blank to match any type.">
                            <Select value={form.hearing_type} onChange={e => setForm({...form, hearing_type: e.target.value})}
                                options={[{ value: "", label: "— any —" }, ...opts.hearings]} data-testid="firm-vc-hearing-select"/>
                        </Field>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                        Don't see a value? Add it under <span className="text-primary">Settings → Master data</span> first (Courts, Categories, Benches, Hearing types).
                        More-specific entries automatically win when multiple match.
                    </p>
                    {editId && (
                        <Field label="Reason for change *" hint="Audit log requirement (min 5 characters)">
                            <Input required minLength="5" value={form.reason || ""} onChange={e => setForm({...form, reason: e.target.value})}
                                placeholder="e.g. Court updated bench-2 VC URL on 09 Feb 2026 circular"
                                data-testid="firm-vc-reason-input"/>
                        </Field>
                    )}
                    <div className="flex justify-end pt-2"><GoldButton disabled={saving || !form.court} data-testid="firm-vc-save-btn">{saving ? "..." : "Save"}</GoldButton></div>
                </form>
            </Modal>
        </>
    );
}
